<script lang="ts" setup>
import { computed } from "vue";
import { useDensity } from "../../composables/density.js";
import {
  SKELETON_INJECTED_SYMBOL,
  useSkeletonContext,
} from "../../composables/useSkeletonState.js";
import { extractLinkProps } from "../../utils/router.js";
import OnyxRouterLink from "../OnyxRouterLink/OnyxRouterLink.vue";
import OnyxSkeleton from "../OnyxSkeleton/OnyxSkeleton.vue";
import { useTocContext } from "../OnyxTableOfContents/useTocContext.js";
import type { OnyxTableOfContentsItemProps } from "./types.js";

const props = withDefaults(defineProps<OnyxTableOfContentsItemProps>(), {
  active: "auto",
  level: 1,
  skeleton: SKELETON_INJECTED_SYMBOL,
});

const slots = defineSlots<{
  /**
   * Item content.
   */
  default(): unknown;
  /**
   * Optional nested children to display. Recommended to use the `OnyxTableOfContentsItem` component
   * here.
   */
  children?(): unknown;
}>();

const { densityClass } = useDensity(props);

const link = computed(() => extractLinkProps(props.link));
const skeleton = useSkeletonContext(props);

const { isVisible } = useTocContext({ href: computed(() => link.value.href) });

const isActive = computed(() => {
  if (props.active !== "auto") return props.active;
  return isVisible.value;
});
</script>

<template>
  <li
    v-if="skeleton"
    :class="['onyx-component', 'onyx-toc-item-skeleton', densityClass, 'onyx-text']"
  >
    <OnyxSkeleton class="onyx-toc-item-skeleton__content" />
  </li>

  <li v-else :class="['onyx-component', 'onyx-toc-item', densityClass, 'onyx-text']">
    <OnyxRouterLink
      :class="[
        'onyx-toc-item__link',
        'onyx-truncation-ellipsis',
        { 'onyx-router-link--active': isActive },
      ]"
      :href="link.href"
      :target="link.target"
    >
      <slot></slot>
    </OnyxRouterLink>

    <ul v-if="!!slots.children" class="onyx-toc-item__children">
      <slot name="children"></slot>
    </ul>
  </li>
</template>

<style lang="scss">
@use "../../styles/mixins/layers.scss";

.onyx-toc-item,
.onyx-toc-item-skeleton {
  @include layers.component() {
    --onyx-toc-item-padding-block: var(--onyx-density-2xs);
    --onyx-toc-item-padding-inline: var(--onyx-density-xs);
  }
}

.onyx-toc-item-skeleton {
  @include layers.component() {
    list-style: none;

    &__content {
      height: calc(1lh + 2 * var(--onyx-toc-item-padding-block));
      width: 100%;
    }
  }
}

.onyx-toc-item {
  @include layers.component() {
    --onyx-toc-item-background-hover: var(--onyx-color-base-neutral-200);
    --onyx-toc-item-background-focus: transparent;
    --onyx-toc-item-color: var(--onyx-color-text-icons-neutral-medium);
    --onyx-toc-item-color-hover: var(--onyx-color-text-icons-neutral-intense);
    --onyx-toc-item-color-outline: var(--onyx-color-component-focus-neutral);
    --onyx-toc-item-indicator-color: var(--onyx-color-component-border-neutral);
    --onyx-toc-item-indicator-width: var(--onyx-1px-in-rem);
    --onyx-toc-item-indentation: var(--onyx-density-xs);
    list-style: none;
    position: relative;

    &__link {
      display: block;
      padding: var(--onyx-toc-item-padding-block) var(--onyx-toc-item-padding-inline);
      border-radius: var(--onyx-radius-sm);
      font-family: var(--onyx-font-family-paragraph);
      color: var(--onyx-toc-item-color);

      &:hover {
        background-color: var(--onyx-toc-item-background-hover);
        color: var(--onyx-toc-item-color-hover);
        --onyx-toc-item-indicator-color: var(--onyx-color-component-border-neutral-hover);
      }

      &:focus-visible {
        background-color: var(--onyx-toc-item-background-focus);
        color: var(--onyx-toc-item-color-hover);
        outline: var(--onyx-outline-width) solid var(--onyx-toc-item-color-outline);
      }

      &.onyx-router-link--active {
        --onyx-toc-item-background-focus: var(--onyx-toc-item-background-hover);
        --onyx-toc-item-color: var(--onyx-color-text-icons-primary-intense);
        --onyx-toc-item-color-hover: var(--onyx-color-text-icons-primary-bold);
        --onyx-toc-item-color-outline: var(--onyx-color-component-focus-primary);
        --onyx-toc-item-indicator-color: var(--onyx-color-component-border-primary);
        font-family: var(--onyx-font-family-h3);
        font-weight: var(--onyx-font-weight-semibold);
      }
    }

    &__children {
      margin-top: var(--onyx-toc-list-gap, 0rem);
      padding: 0;
      list-style: none;
      display: flex;
      flex-direction: column;
      gap: var(--onyx-toc-list-gap, 0rem);

      .onyx-toc-item__link {
        padding-left: calc(var(--onyx-spacing-2xs) + var(--onyx-toc-item-indentation));

        &::before {
          content: "";
          width: var(--onyx-toc-item-indicator-width);
          height: calc(100% + var(--onyx-toc-list-gap, 0rem));
          background-color: var(--onyx-toc-item-indicator-color);
          display: block;
          position: absolute;
          left: calc(
            var(--onyx-toc-item-indicator-width) + var(--onyx-1px-in-rem) +
              var(--onyx-toc-item-indentation)
          );
          top: calc(-0.5 * var(--onyx-toc-list-gap, 0rem));
        }
      }
    }
  }
}
</style>
